import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCurrentUser } from "../../auth";
import { useApi } from "../../api";
import { trackAccountCreationConversion } from "../../analytics";
import {
  Alert,
  Button,
  Field,
  Input,
  Loading,
  Select,
  SelectItem,
} from "../../components/ui";
import { Feedback, LoadError, useAction, useResource } from "./shared";

export function OrganizationSettings() {
  const { t } = useTranslation("settings");
  const api = useApi();
  const navigate = useNavigate();
  const {
    organizationSetupRequired: setup,
    reloadCurrentUser,
    currentUser,
  } = useCurrentUser();
  const remote = useResource<{ id: string; name: string }>(
    setup ? null : "/organizations/me",
  );
  const [name, setName] = useState("");
  const action = useAction();
  useEffect(() => {
    if (remote.data) setName(remote.data.name);
  }, [remote.data]);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim()) return;
    void action.run(async () => {
      const response = await api.put("/organizations/me", {
        name: name.trim(),
      });
      if (setup) trackAccountCreationConversion(response?.id);
      await reloadCurrentUser();
      if (setup) navigate("/products", { replace: true });
      else await remote.reload();
    }, t("organization.saved"));
  };
  return (
    <section className="settings-section settings-form-width">
      <h2>{setup ? t("organization.start") : t("sections.organization")}</h2>
      {setup && <p className="settings-muted">{t("organization.startHint")}</p>}
      {setup && currentUser?.referral_partner_name && (
        <Alert variant="info">
          {t("organization.referral", {
            partner: currentUser.referral_partner_name,
          })}
        </Alert>
      )}
      <LoadError {...remote} />
      <Feedback {...action} />
      {remote.loading ? (
        <Loading label={t("common.loading")} />
      ) : (
        <form onSubmit={submit} className="settings-form">
          <Field label={t("organization.name")} htmlFor="organization-name">
            <Input
              id="organization-name"
              autoComplete="organization"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={200}
              disabled={action.busy}
            />
          </Field>
          <div>
            <Button
              type="submit"
              disabled={
                action.busy ||
                !name.trim() ||
                (!setup && name === remote.data?.name)
              }
            >
              {action.busy
                ? t("common.saving")
                : setup
                  ? t("organization.create")
                  : t("common.save")}
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}

type Policy = {
  minLength?: number;
  hasUppercase?: boolean;
  hasLowercase?: boolean;
  hasNumber?: boolean;
  hasSymbol?: boolean;
};
export function AccountSettings() {
  const { t, i18n } = useTranslation("settings");
  const { currentUser } = useCurrentUser();
  const api = useApi();
  const remote = useResource<{ policy: Policy }>("/me/password-policy");
  const action = useAction();
  const [oldPassword, setOld] = useState("");
  const [newPassword, setNew] = useState("");
  const [confirm, setConfirm] = useState("");
  const [validation, setValidation] = useState("");
  const passwordPolicy = remote.data?.policy;
  const policyHint = [
    passwordPolicy?.minLength
      ? t("account.minLength", { count: passwordPolicy.minLength })
      : "",
    passwordPolicy?.hasUppercase ? t("account.uppercase") : "",
    passwordPolicy?.hasLowercase ? t("account.lowercase") : "",
    passwordPolicy?.hasNumber ? t("account.number") : "",
    passwordPolicy?.hasSymbol ? t("account.symbol") : "",
  ]
    .filter(Boolean)
    .join(" ");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const p = remote.data?.policy;
    const issues: string[] = [];
    if (newPassword !== confirm) issues.push(t("account.mismatch"));
    if (newPassword === oldPassword) issues.push(t("account.samePassword"));
    if (p?.minLength && newPassword.length < p.minLength)
      issues.push(t("account.minLength", { count: p.minLength }));
    if (p?.hasUppercase && !/[A-Z]/.test(newPassword))
      issues.push(t("account.uppercase"));
    if (p?.hasLowercase && !/[a-z]/.test(newPassword))
      issues.push(t("account.lowercase"));
    if (p?.hasNumber && !/\d/.test(newPassword))
      issues.push(t("account.number"));
    if (p?.hasSymbol && !/[^\w\s]/.test(newPassword))
      issues.push(t("account.symbol"));
    setValidation(issues.join(" "));
    if (issues.length) return;
    void action.run(async () => {
      await api.post("/me/set-password", {
        old_password: oldPassword,
        new_password: newPassword,
      });
      setOld("");
      setNew("");
      setConfirm("");
    }, t("account.passwordSaved"));
  };
  return (
    <section className="settings-section settings-form-width">
      <h2>{t("sections.account")}</h2>
      <p className="settings-muted">{currentUser?.email}</p>
      <Field htmlFor="interface-language" label={t("account.language")}>
        <Select
          id="interface-language"
          value={i18n.language.startsWith("pl") ? "pl" : "en"}
          onValueChange={(value) => void i18n.changeLanguage(value)}
        >
          <SelectItem value="en">English</SelectItem>
          <SelectItem value="pl">Polski</SelectItem>
        </Select>
      </Field>
      <div className="settings-divider" />
      <h3>{t("account.password")}</h3>
      <LoadError {...remote} />
      <Feedback {...action} />
      {remote.loading && !remote.data && (
        <Loading label={t("common.loading")} />
      )}
      {validation && <Alert variant="error">{validation}</Alert>}
      <form onSubmit={submit} className="settings-form">
        <Field label={t("account.currentPassword")} htmlFor="current-password">
          <Input
            id="current-password"
            type="password"
            autoComplete="current-password"
            value={oldPassword}
            onChange={(e) => setOld(e.target.value)}
            required
            disabled={action.busy}
          />
        </Field>
        <Field
          label={t("account.newPassword")}
          htmlFor="new-password"
          hint={policyHint || undefined}
        >
          <Input
            id="new-password"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNew(e.target.value)}
            required
            disabled={action.busy}
          />
        </Field>
        <Field label={t("account.confirmPassword")} htmlFor="confirm-password">
          <Input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            disabled={action.busy}
            aria-invalid={Boolean(validation)}
          />
        </Field>
        <div>
          <Button
            type="submit"
            disabled={action.busy || !oldPassword || !newPassword || !confirm}
          >
            {action.busy ? t("common.saving") : t("account.changePassword")}
          </Button>
        </div>
      </form>
    </section>
  );
}
